import Layout from '@/components/Layout';
import api from '@/services/api';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import { Suspense, lazy, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import CustomerCard from './CustomerCard';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import Fallback from '@/components/Fallback';
import Fab from '@mui/material/Fab';
import { FilterIcon } from 'lucide-react';
import FilterCard, { type MapFilterQuery } from './FilterCard';
const DevicesMap = lazy(() => import('@components/MapboxMap/DevicesMap'));

type MapLocationType = {
  lon: number;
  lat: number;
  id: number;
  online: boolean;
};

export default function Map() {
  const data = useLocation();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [locales, setLocales] = useState<MapLocationType[]>([]);
  const [loading, setLoading] = useState(true);

  const [selectedDevice, setSelectedDevice] = useState<DeviceType | null>(null);
  const [cardOpen, setCardOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);

  const [selectedFilter, setSelectedFilter] = useState<MapFilterQuery>(null);

  useEffect(() => {
    if (data.state?.data) {
      setSelectedDevice(data.state.data);
      setCardOpen(true);
    }
  }, [data.state]);

  const center = data.state?.data.customerData
    ? ([data.state.data.customerData.lon, data.state.data.customerData.lat] as
        | [number, number]
        | undefined)
    : undefined;

  const fetchDeviceData = async (id: number) => {
    try {
      const response = await api.get(`/devices/${id}`);
      return response.data as DeviceType;
    } catch (error) {
      console.error('Error fetching device data:', error);
    }
  };

  const handleMarkerClick = async (id: number) => {
    setFilterOpen(false);
    const deviceData = await fetchDeviceData(id);
    setSelectedDevice(deviceData || null);
    setCardOpen(true);
  };

  const handleFilterOpen = () => {
    setSelectedDevice(null);
    setCardOpen(false);
    setFilterOpen(true);
  };

  const closeCard = () => {
    setSelectedDevice(null);
    setCardOpen(false);
  };

  // fetch all customerData on component mount
  useEffect(() => {
    setLoading(true);
    const fetchCustomerData = async () => {
      try {
        const response = await api.get('/devices/map', {
          params: selectedFilter || {},
        });
        setLocales(
          response.data.filter(
            (device: MapLocationType) => device.lat && device.lon
          )
        );
      } catch (err) {
        enqueueSnackbar(t('map.failedFetch') + err, {
          variant: 'error',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchCustomerData();
  }, [selectedFilter]);

  return (
    <Layout
      noLeftMargin
      sx={{
        height: '100%',
        padding: 0,
      }}
    >
      <Stack direction="row" sx={{ width: '100%', height: '100%' }}>
        <Stack
          sx={{
            flex: '0 0 100%',
            height: '100%',
          }}
        >
          {loading ? (
            <Skeleton variant="rectangular" width="100%" height="100%" />
          ) : (
            <Suspense
              fallback={<Fallback text={t('map.loading')} variant="compact" />}
            >
              <DevicesMap
                locales={locales}
                center={center}
                onMarkerClick={handleMarkerClick}
              />
            </Suspense>
          )}
        </Stack>
        <Fab
          aria-label="filter"
          sx={{ position: 'absolute', bottom: 32, right: 16 }}
          onClick={handleFilterOpen}
        >
          <FilterIcon />
        </Fab>
        {selectedDevice && (
          <CustomerCard
            info={selectedDevice!}
            open={cardOpen}
            setOpen={closeCard}
          />
        )}
        {filterOpen && (
          <FilterCard
            filterOpen={filterOpen}
            setFilterOpen={setFilterOpen}
            selectedFilter={selectedFilter}
            setSelectedFilter={setSelectedFilter}
          />
        )}
      </Stack>
    </Layout>
  );
}
